import React__default from 'react';
import classnames from 'classnames';
import { getMonth, getYear, isValid } from 'date-fns';
import { Tooltip } from '@grandvisionhq/common';
import { Icon } from '@grandvisionhq/icons';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { DateUtils } from '@grandvisionhq/utils';
import ReactDatePicker from './ReactDatePicker.js';

const createCustomHeader = (dateFnsLocale)=>{
    return function({ date , decreaseMonth , increaseMonth , prevMonthButtonDisabled , nextMonthButtonDisabled  }) {
        var ref;
        return /*#__PURE__*/ React__default.createElement("div", {
            className: "react-datepicker__custom-nav"
        }, /*#__PURE__*/ React__default.createElement("button", {
            className: "react-datepicker__navigation react-datepicker__navigation--previous",
            onClick: decreaseMonth,
            disabled: prevMonthButtonDisabled,
            type: "button"
        }, /*#__PURE__*/ React__default.createElement(Icon, {
            name: "chevron-left"
        })), /*#__PURE__*/ React__default.createElement("div", {
            className: "react-datepicker__current-month"
        }, (dateFnsLocale === null || dateFnsLocale === void 0 ? void 0 : (ref = dateFnsLocale.localize) === null || ref === void 0 ? void 0 : ref.month(getMonth(date))) || getMonth(date), " ", getYear(date)), /*#__PURE__*/ React__default.createElement("button", {
            className: "react-datepicker__navigation react-datepicker__navigation--next",
            onClick: increaseMonth,
            disabled: nextMonthButtonDisabled,
            type: "button"
        }, /*#__PURE__*/ React__default.createElement(Icon, {
            name: "chevron-right"
        })));
    };
};
const DatePicker = ({ onChange , onBlur , className , label , tooltip , placeholder , optionalText , errorMessage , initialValue , minDate , maxDate , dateFormat , disabled =false , required =true , hideAsteriskSign =false , useNativeInput =false , 'data-t': testId = 'date-picker' ,  })=>{
    const { getLabel , locale , dateFnsLocale  } = useIntl();
    const { featureConfigs  } = useFeatureToggles();
    const { datePickerDateFormat  } = featureConfigs;
    const [date1, setDate] = React__default.useState(initialValue);
    const [isValid$1, setIsValid] = React__default.useState(true);
    const [hasFocus, setHasFocus] = React__default.useState(false);
    const [isTouched, setIsTouched] = React__default.useState(false);
    const customHeader = React__default.useMemo(()=>createCustomHeader(dateFnsLocale)
    , [
        dateFnsLocale
    ]);
    const handleKeyDown = React__default.useCallback((event)=>{
        if (event.key === 'Escape') {
            event.stopPropagation();
            setHasFocus(false);
        }
    }, [
        setHasFocus
    ]);
    const handleBlur = React__default.useCallback(()=>{
        setHasFocus(false);
        onBlur && onBlur();
    }, [
        onBlur
    ]);
    const handleFocus = React__default.useCallback(()=>{
        if (!disabled) {
            setHasFocus(true);
        }
    }, [
        disabled
    ]);
    const handleChange = React__default.useCallback((date)=>{
        setDate(date || undefined);
        !isTouched && setIsTouched(true);
    }, [
        isTouched
    ]);
    const onNativeChange = React__default.useCallback((e)=>{
        if (!e.target.value) {
            /*
         * Clearing the input is not supported by the current interfaces,
         * fallback to the current value. This shouldn't happen since
         * we hide the clear button with css as well.
         */ e.target.value = date1 && DateUtils.toLocalDate(date1);
            return;
        }
        handleChange(new Date(e.target.value));
    }, [
        handleChange
    ]);
    React__default.useEffect(()=>{
        if (date1 && isValid(date1)) {
            onChange(date1);
            setHasFocus(false);
            isTouched && setIsValid(true);
        } else if (required) {
            isTouched && setIsValid(false);
        }
    }, [
        date1,
        onChange,
        required,
        isTouched
    ]);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('form-control date-picker', className, {
            'date-picker--focus': hasFocus,
            'date-picker--disabled': disabled
        }),
        "data-t": testId
    }, label && /*#__PURE__*/ React__default.createElement("label", {
        className: "form-control__label"
    }, label, required && !hideAsteriskSign && /*#__PURE__*/ React__default.createElement("span", {
        className: "form-control__asterisk"
    }, "*"), tooltip && /*#__PURE__*/ React__default.createElement(Tooltip, {
        message: tooltip,
        id: "date-picker-tooltip"
    })), !required && optionalText && /*#__PURE__*/ React__default.createElement("small", {
        className: "form-control__optional-tag"
    }, optionalText), /*#__PURE__*/ React__default.createElement("div", {
        className: "date-picker__input-container"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "calendar"
    }), useNativeInput ? /*#__PURE__*/ React__default.createElement("input", {
        type: "date",
        className: classnames('form-control__input', 'date-picker__input', 'date-picker__input--native', {
            _is_invalid: !isValid$1
        }),
        value: date1 && DateUtils.toLocalDate(date1),
        min: minDate && DateUtils.toLocalDate(minDate),
        max: maxDate && DateUtils.toLocalDate(maxDate),
        onClick: handleFocus,
        onChange: onNativeChange,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
        disabled: disabled,
        required: required,
        "aria-required": required,
        "aria-label": getLabel('common.selectDate')
    }) : dateFnsLocale && /*#__PURE__*/ React__default.createElement(ReactDatePicker, {
        dateFnsLocale: dateFnsLocale,
        className: classnames('form-control__input', 'date-picker__input', {
            _is_invalid: !isValid$1
        }),
        selected: date1,
        onFocus: handleFocus,
        onChange: handleChange,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
        placeholderText: placeholder,
        dateFormat: dateFormat !== null && dateFormat !== void 0 ? dateFormat : datePickerDateFormat,
        locale: locale,
        minDate: minDate,
        maxDate: maxDate,
        disabled: disabled,
        renderCustomHeader: customHeader,
        customInput: /*#__PURE__*/ React__default.createElement("input", {
            placeholder: placeholder,
            "aria-label": getLabel('common.selectDate')
        })
    }), /*#__PURE__*/ React__default.createElement(Icon, {
        name: "chevron-down"
    })), errorMessage && !isValid$1 && /*#__PURE__*/ React__default.createElement("span", {
        className: "form-control__error"
    }, errorMessage));
};
var DatePicker$1 = DatePicker;

export { DatePicker, createCustomHeader, DatePicker$1 as default };
//# sourceMappingURL=DatePicker.js.map
